import { Link } from "react-router-dom";
import { formatIDR } from "@/lib/format";
import { Star, Tag, Heart } from "lucide-react";
import { useCart } from "@/pages/store/StoreLayout";
import { toast } from "sonner";

export default function ProductCard({ p, testid }) {
  const { toggleWish, wishlist, addToCart } = useCart();
  const wished = wishlist.find((w) => w.id === p.id);
  const price = p.promo_price ?? p.price;
  const hasPromo = (p.promo_percent || 0) > 0 && price < p.price;
  const hasBundle = (p.bundle_min_qty || 0) > 0 && (p.bundle_percent || 0) > 0;
  const grosirTiers = [...(p.grosir_tiers || [])].sort((a, b) => a.min_qty - b.min_qty);
  const hasGrosir = grosirTiers.length > 0;
  const bestTier = hasGrosir ? [...grosirTiers].sort((a, b) => b.percent - a.percent)[0] : null;
  const grosirFrom = bestTier ? Math.round(price * (1 - bestTier.percent / 100)) : null;
  const sold = p.sold_total ?? p.sold ?? 0;
  const soldTxt = sold >= 1000 ? ((sold / 1000).toFixed(1).replace(".0", "") + "RB+") : `${sold}`;
  const rating = p.rating_avg ?? p.rating ?? "5.0";

  return (
    <div data-testid={testid || `product-card-${p.id}`} className="bg-white rounded-xl overflow-hidden border border-blush-soft flex flex-col">
      <div className="relative aspect-[3/4] overflow-hidden">
        <Link to={`/store/product/${p.id}`}>
          <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
        </Link>
        {hasPromo && (
          <span className="absolute bottom-0 left-0 bg-amber-400 text-amber-900 text-[9px] font-bold px-1.5 py-0.5 rounded-tr-lg">PROMO</span>
        )}
        {hasGrosir && (
          <span data-testid={`grosir-badge-${p.id}`} className="absolute top-2 left-0 bg-emerald-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-r-lg">GROSIR</span>
        )}
        {hasBundle && (
          <span data-testid={`bundle-badge-${p.id}`} className={`absolute ${hasGrosir ? "top-8" : "top-2"} left-0 bg-brand text-white text-[8px] font-bold px-1.5 py-0.5 rounded-r-lg`}>
            Bundle {p.bundle_min_qty}+ -{p.bundle_percent}%
          </span>
        )}
        <button data-testid={`wish-${p.id}`} onClick={() => toggleWish(p)}
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
          <Heart className={`w-4 h-4 ${wished ? "fill-brand text-brand" : "text-cocoa-muted"}`} />
        </button>
      </div>
      <div className="p-2.5 flex flex-col flex-1">
        <Link to={`/store/product/${p.id}`}>
          <div className="text-[11px] font-medium leading-snug line-clamp-2 min-h-8">
            <span className="text-[8px] bg-brand text-white font-bold px-1 py-0.5 rounded mr-1 align-middle">Official</span>
            {p.name}
          </div>
        </Link>
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
          <span className="text-brand font-bold text-sm">{formatIDR(price)}</span>
          {hasPromo && <span className="text-[9px] text-cocoa-muted line-through">{formatIDR(p.price)}</span>}
          {hasPromo && <span className="text-[9px] text-red-500 font-bold">-{Math.round(p.promo_percent)}%</span>}
          {p.voucher_available && (
            <span data-testid={`voucher-tag-${p.id}`} className="flex items-center gap-0.5 text-[8px] font-bold text-brand border border-brand/40 rounded px-1 py-px">
              <Tag className="w-2.5 h-2.5" /> Voucher
            </span>
          )}
        </div>
        {hasGrosir && (
          <div data-testid={`grosir-info-${p.id}`} className="text-[9px] text-emerald-700 font-bold mt-1">Beli {grosirTiers[0].min_qty}+ mulai {formatIDR(grosirFrom)}/pcs</div>
        )}
        <div className="flex items-center gap-1 mt-auto pt-1.5 text-[10px] text-cocoa-muted">
          <span className="flex items-center gap-0.5 border border-amber-300 rounded px-1 py-px text-amber-600 font-semibold">
            <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />{rating}
          </span>
          <span>({p.review_count ?? 0})</span>
          <span>•</span>
          <span data-testid={`sold-${p.id}`}>{soldTxt} terjual</span>
          <button data-testid={`add-cart-${p.id}`}
            onClick={() => { addToCart({ ...p, price }); toast.success(`${p.name} masuk keranjang`); }}
            className="ml-auto w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center text-sm leading-none">+</button>
        </div>
      </div>
    </div>
  );
}
